import {Icon} from "@/components/icon/Icon.tsx";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {timezoneAbbr} from "@/lib/format.tsx";
import {
  IncidentState,
  Incident as IncidentType,
  StatusPageLayoutPreset,
  StatusPageTimeFormat,
} from "@/lib/types/statuspages.ts";
import {TabParams} from "@/pages/statuspages/consumption/types.ts";
import {isTwoColumnLayout} from "@/pages/statuspages/consumption/utils/layout.ts";
import {formatDurationRounded} from "@/pages/statuspages/consumption/utils/uptimeBarEvents.ts";
import classNames from "classnames";
import {formatInTimeZone} from "date-fns-tz";
import {parseISO} from "date-fns/parseISO";
import {useEffect, useRef, useState} from "react";
import {FiChevronDown, FiChevronRight} from "react-icons/fi";
import {useSearchParams} from "react-router";
import styles from "./IncidentItem.module.scss";

export interface IncidentItemProps {
  incident: IncidentType;
  componentStatusRank: Record<string, number>;
  timeFormat?: StatusPageTimeFormat;
  timezone?: string;
  layout?: StatusPageLayoutPreset;
  variant?: "default" | "inspire";
}

const INCIDENT_STATE_TO_PILL: Record<IncidentState, PillStatus> = {
  [IncidentState.IDENTIFIED]: PillStatus.IDENTIFIED,
  [IncidentState.INVESTIGATION]: PillStatus.INVESTIGATING,
  [IncidentState.MAINTENANCE]: PillStatus.MAINTENANCE,
  [IncidentState.MONITORING]: PillStatus.MONITORING,
  [IncidentState.NOTIFICATION]: PillStatus.NOTIFICATION,
  [IncidentState.RESOLVED]: PillStatus.RESOLVED,
};

const AFFECTED_COMPONENT_STATUS_PILL: Record<string, PillStatus> = {
  operational: PillStatus.OPERATIONAL,
  "degraded-performance": PillStatus.DEGRADED_PERFORMANCE,
  "partial-outage": PillStatus.PARTIAL_OUTAGE,
  "major-outage": PillStatus.OUTAGE,
  "under-maintenance": PillStatus.MAINTENANCE,
  notification: PillStatus.NOTIFICATION,
};

export const IncidentItem = ({
  incident,
  timeFormat,
  timezone = "UTC",
  layout = StatusPageLayoutPreset.EXPANDED,
  variant = "default",
}: IncidentItemProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Mirror the open event into the URL so a reloaded or shared link reopens and anchors to it.
  const isTargeted = searchParams.get(TabParams.EVENT) === String(incident.id);
  const [isOpen, setIsOpen] = useState(isTargeted);
  const isTwoColumn = isTwoColumnLayout(layout);

  const containerRef = useRef<HTMLDivElement>(null);
  const didScrollRef = useRef(false);

  useEffect(() => {
    if (isTargeted && !didScrollRef.current && containerRef.current) {
      didScrollRef.current = true;
      containerRef.current.scrollIntoView({behavior: "smooth", block: "center"});
    }
  }, [isTargeted]);

  const handleToggle = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextOpen) {
          next.set(TabParams.EVENT, String(incident.id));
        } else if (next.get(TabParams.EVENT) === String(incident.id)) {
          next.delete(TabParams.EVENT);
        }
        return next;
      },
      {preventScrollReset: true}
    );
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      const timeFmt = timeFormat === StatusPageTimeFormat.TWENTY_FOUR_HOUR ? "HH:mm:ss" : "hh:mm:ss a";
      return `${formatInTimeZone(date, timezone, `MMM d, yyyy ${timeFmt}`)} ${timezoneAbbr(timezone, date)}`;
    } catch {
      return dateString;
    }
  };

  const lastUpdate = incident.updates?.[incident.updates.length - 1];
  const pillStatus =
    INCIDENT_STATE_TO_PILL[incident.latest_update_incident_state] ??
    (lastUpdate?.incident_state !== undefined ? INCIDENT_STATE_TO_PILL[lastUpdate.incident_state] : undefined) ??
    PillStatus.NOTIFICATION;
  const hasAffectedComponents = incident.affected_components && incident.affected_components.length > 0;

  return (
    <div
      ref={containerRef}
      id={`event-${incident.id}`}
      className={classNames(styles.incidentContainer, {
        [styles.incidentContainerInspire]: variant === "inspire",
        [styles.open]: isOpen,
        [styles.compact]: isTwoColumn,
        [styles.compactExpanded]: isTwoColumn && isOpen,
      })}
    >
      <div
        className={classNames(styles.incident, {[styles.open]: isOpen})}
        role="button"
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <div className={styles.incidentHeading}>
          <div className={classNames(styles.incidentInfo, {[styles.compact]: isTwoColumn})}>
            <span className={styles.name}>{incident.name}</span>
            <span className={styles.date}>
              {formatDateTime(incident.starts_at)} — {incident.ends_at ? formatDateTime(incident.ends_at) : "Ongoing"}{" "}
              <span className={styles.dateDuration}>({formatDurationRounded(incident.duration)})</span>
            </span>
          </div>
          <div className={styles.incidentRowActions}>
            <StatusPill type="incident" status={pillStatus} variant="textAndIcon" />
            <span className={styles.incidentChevron}>
              <Icon icon={isOpen ? FiChevronDown : FiChevronRight} size="normal" />
            </span>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className={styles.incidentDetails}>
          <div className={styles.incidentDetailsContent}>
            <div className={classNames(styles.updatesTimeline, {[styles.fullWidth]: !hasAffectedComponents})}>
              {incident.updates && incident.updates.length > 0 ? (
                incident.updates.map((update) => (
                  <div key={update.id} className={styles.update}>
                    <div className={styles.updateLine}>
                      <div className={styles.updateLineIcon}>
                        <StatusPill
                          type="incident"
                          status={INCIDENT_STATE_TO_PILL[update.incident_state] ?? PillStatus.NOTIFICATION}
                          variant="icon"
                        />
                      </div>
                      <div className={styles.updateLineConnector} />
                    </div>
                    <div className={styles.updateBody}>
                      <div className={styles.updateHeading}>
                        <span className={styles.updateStateName}>{update.incident_state_display}</span>
                        <span className={styles.updateDate}>
                          {formatDateTime(update.updated_at || incident.starts_at)}
                        </span>
                      </div>
                      {(update.description_html || update.description) && (
                        <div
                          className={classNames(styles.updateDescription, styles.markdown)}
                          dangerouslySetInnerHTML={{__html: update.description_html || update.description}}
                        />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.update}>
                  <div className={styles.updateLine} />
                  <div className={styles.updateBody}>
                    <div className={styles.updateHeading}>
                      <span className={styles.updateStateName}>No updates available</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {hasAffectedComponents && (
              <div className={styles.affectedComponents}>
                <span className={styles.affectedComponentsTitle}>Affected Components:</span>
                <ul>
                  {incident.affected_components.map((component) => (
                    <li key={component.id} className={styles.affectedComponentItem}>
                      <StatusPill
                        type="checks"
                        status={AFFECTED_COMPONENT_STATUS_PILL[component.status] ?? PillStatus.OPERATIONAL}
                        variant="icon"
                      />
                      <span>{component.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
